import * as THREE from 'three';
import type { Input } from '../engine/Input';
import type { Terrain } from '../world/Terrain';
import type { Player } from './Player';
import type { FollowCamera } from './FollowCamera';
import type { CharacterConfig } from '../data/characters';

export type MoveState = 'ground' | 'air' | 'fly';

export class Controller {
  readonly position = new THREE.Vector3(0, 0, -18);
  private velY = 0;
  state: MoveState = 'ground';
  private moving = false;
  private facing = 0;

  constructor(
    private input: Input,
    private terrain: Terrain,
    private player: Player,
    private camera: FollowCamera,
    private character: () => CharacterConfig,
  ) {
    this.player.root.position.copy(this.position);
  }

  setCharacterType(): void {
    // if we become an elf mid-flight, drop to air gracefully
  }

  tick(dt: number): void {
    const speed = this.state === 'fly' ? 8 : (this.input.isDown('shift') ? 7 : 4);
    const yaw = this.camera.currentYaw;

    const forward = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const move = new THREE.Vector3();
    if (this.input.isDown('w')) move.add(forward);
    if (this.input.isDown('s')) move.sub(forward);
    if (this.input.isDown('d')) move.add(right);
    if (this.input.isDown('a')) move.sub(right);

    this.moving = move.lengthSq() > 0.0001;
    if (this.moving) {
      move.normalize().multiplyScalar(speed * dt);
      this.position.x += move.x;
      this.position.z += move.z;
      this.facing = Math.atan2(move.x, move.z);
    }

    // Flight toggle: Space midair = fly; Space again while flying = keep ascending.
    if (this.input.consumePress(' ')) {
      if (this.state === 'ground') {
        this.velY = 6; // jump
        this.state = 'air';
      } else if (this.state === 'air') {
        this.state = 'fly';
        this.velY = 0;
      }
      // While flying, hold Space handled below.
    }

    if (this.state === 'fly') {
      let vy = 0;
      if (this.input.isDown(' ')) vy += 6;
      if (this.input.isDown('shift')) vy -= 6;
      this.position.y += vy * dt;
      // Tiny float bob
      this.position.y += Math.sin(performance.now() * 0.003) * 0.004;
      // Exit flight if near ground
      const terrainY = this.terrain.heightAt(this.position.x, this.position.z);
      if (this.position.y < terrainY + 0.1) {
        this.position.y = terrainY;
        this.state = 'ground';
        this.velY = 0;
      }
    } else {
      // Gravity + ground
      this.velY -= 18 * dt;
      this.position.y += this.velY * dt;
      const terrainY = this.terrain.heightAt(this.position.x, this.position.z);
      if (this.position.y <= terrainY) {
        this.position.y = terrainY;
        this.velY = 0;
        this.state = 'ground';
      } else {
        if (this.state === 'ground') this.state = 'air';
      }
    }

    // Clamp world bounds
    this.position.x = THREE.MathUtils.clamp(this.position.x, -95, 95);
    this.position.z = THREE.MathUtils.clamp(this.position.z, -95, 95);
    this.position.y = Math.min(this.position.y, 40);

    this.player.root.position.copy(this.position);
    // Smooth facing
    if (this.moving) {
      const cur = this.player.root.rotation.y;
      const diff = ((this.facing - cur + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      this.player.root.rotation.y = cur + diff * Math.min(1, dt * 10);
    }
    this.player.animate(dt, this.moving, this.state === 'fly');
  }
}
